import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Scan, Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1711409645921-ef3db0501f96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxicmFpbiUyMHNjYW4lMjBjb21wdXRlcnxlbnwwfHx8fDE3ODkwNzE5MDV8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("demo@x-gen.ai");
    setPassword("Demo#User2026");
  };

  return (
    <div className="min-h-screen bg-ink grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative overflow-hidden border-r border-slate-200">
        <img src={SIDE_IMG} alt="Neural mesh visualization" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/60" aria-hidden="true" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-4">X-GEN.AI</p>
          <p className="font-display text-3xl font-black uppercase tracking-tighter leading-tight">
            The reading room,
            <br />
            reimagined.
          </p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 md:px-16 lg:px-24 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mute hover:text-slate-900 transition-colors duration-300 mb-12" data-testid="login-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>

          <div className="flex items-center gap-2 mb-8">
            <span className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
              <Scan className="w-4.5 h-4.5 text-white" />
            </span>
            <span className="font-display font-extrabold tracking-tight text-xl">X-GEN.AI</span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Clinician sign in</h1>
          <p className="text-mute text-sm mb-10">Access your diagnostic dashboard and scan worklist.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="login-email">Email</label>
              <Input id="login-email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@hospital.org" required className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="login-password">Password</label>
              <Input id="login-password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>
            <div className="text-right -mt-2">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute hover:text-brand transition-colors duration-300">
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="text-pulse text-sm font-mono" data-testid="login-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-form-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Authenticating..." : "Sign in"}
            </button>
          </form>

          <button onClick={fillDemo} data-testid="login-demo-fill-button" className="mt-4 w-full border border-slate-200 font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 text-mute hover:border-brand hover:text-slate-900 transition-colors duration-300">
            Use demo account
          </button>

          <p className="mt-8 text-sm text-mute">
            No account yet?{" "}
            <Link to="/register" className="text-brand hover:text-[#0F172A] transition-colors duration-300" data-testid="login-register-link">
              Request access
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
